import { useInvalidate } from '@refinedev/core';
import { Query, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

const preservedQueryKeyFragments = [
  'auth',
  'authenticated',
  'identity',
  'getIdentity',
  'userIdentity',
  'check',
];
const shouldResetQuery = (query: Query) => {
  const queryKey = query.queryKey.map(key => String(key).toLowerCase());

  // Preserva queries de sesion/identidad para que un cambio de contexto no reinicie el usuario.
  return !queryKey.some(key =>
    preservedQueryKeyFragments.some(fragment => key.includes(fragment.toLowerCase())),
  );
};

export const useResetQueriesOnContextChange = () => {
  const invalidate = useInvalidate();
  const queryClient = useQueryClient();
  // Preparación de queries para un cambio de contexto: cancela queries en curso y elimina queries inactivas que podrían contener datos de un contexto diferente al nuevo seleccionado.
  const prepareQueriesForContextChange = useCallback(async () => {
    await queryClient.cancelQueries({ predicate: shouldResetQuery }, { silent: true });

    queryClient.removeQueries({ predicate: query => shouldResetQuery(query) && !query.isActive() });
  }, [queryClient]);
  const clearInactiveQueriesOnContextChange = useCallback(() => {
    queryClient.removeQueries({ predicate: query => shouldResetQuery(query) && !query.isActive() });
  }, [queryClient]);
  const resetQueriesOnContextChange = useCallback(async () => {
    await prepareQueriesForContextChange();

    // Fuerza a las queries activas a recomputarse desde estado inicial y mostrar sus loadings.
    await queryClient.resetQueries({ predicate: shouldResetQuery, type: 'active' });

    // Mantiene compatibilidad con Refine para invalidaciones de recursos CRUD.
    await invalidate({ invalidates: ['all'] });
  }, [invalidate, prepareQueriesForContextChange, queryClient]);

  return {
    clearInactiveQueriesOnContextChange,
    prepareQueriesForContextChange,
    resetQueriesOnContextChange,
  };
};
